import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import SegmentEditor from "../SegmentEditor";
import SnapsWiseAudio from "./SnapsWiseAudio";
import SentencePracticeBlock from "./SentencePracticeBlock";
import PracticeControlsPanel from "./PracticeControlsPanel";
import IntonationComparisonPanel from "./IntonationComparisonPanel";
import AIAssessmentPanel from "./AIAssessmentPanel";
import RateMeOverlay from "./RateMeOverlay";
import WriteEchoModePanel from "./WriteEchoModePanel";

const PracticeModeView = ({
  audioref,
  fullaudio,
  timeUpdate,
  chapterTitle,
  segments,
  segment,
  modelSize,
  renderHelpPill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  sentenceLabel,
  currentWord,
  handleWordPlay,
  handlePlayFromWord,
  handleRangePlay,
  openWordMenu,
  isgranted,
  getMicrophonePermission,
  isPlayingSegment,
  recordingProgress,
  handlePlayPause,
  handleMainAction,
  getButtonConfig,
  rate,
  setRate,
  loading,
  assessment,
  userRecordingUrl,
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  visualizationView,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
  canUsePronunciationApi,
  onWriteEchoStageChange,
}) => {
  const [editedSegments, setEditedSegments] = useState(null);
  const [editedSegment, setEditedSegment] = useState(null);
  const [showRateMeOverlay, setShowRateMeOverlay] = useState(false);
  const [recordingChecked, setRecordingChecked] = useState(false);
  const [isPlayingReview, setIsPlayingReview] = useState(false);
  const [writeEchoStage, setWriteEchoStage] = useState("write");
  const [writeEchoShowText, setWriteEchoShowText] = useState(false);

  const isWriteEcho = practiceMode === "writeEcho";
  const effectivePracticeMode = isWriteEcho ? "echoing" : practiceMode;
  const showTargetText =
    !isWriteEcho ||
    writeEchoStage === "result" ||
    (writeEchoStage === "echo" && writeEchoShowText);
  const showAssessmentResults = !isWriteEcho || writeEchoStage === "result";
  const showMainWordLane =
    !isWriteEcho || writeEchoStage === "echo" || writeEchoStage === "result";

  useEffect(() => {
    setRecordingChecked(false);
    setShowRateMeOverlay(false);
    setIsPlayingReview(false);
  }, [assessment]);

  useEffect(() => {
    setWriteEchoStage("write");
    setWriteEchoShowText(false);
    onWriteEchoStageChange?.("write");
  }, [segment?.id, practiceMode]);

  const changeWriteEchoStage = (stage, details = {}) => {
    setWriteEchoStage(stage);

    if (stage === "echo") {
      setWriteEchoShowText(Boolean(details.showText));
    } else if (stage === "write") {
      setWriteEchoShowText(false);
    }

    onWriteEchoStageChange?.(stage);
  };

  const handleSaveEditedSegments = async (editedSegmentsToSave) => {
    const token = localStorage.getItem("token");
    const requestData = {
      author: subject.author,
      title: subject.title,
      language: subject.language,
      type: subject.type,
      owner: subject.owner,
      file: file.file,
      size_of_model: modelSize,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_USER_API_URL}/whisper/add-edited-segments`,
        { fileinfo: requestData, segments: editedSegmentsToSave },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        alert(`Edited segments ${response.data.action} successfully!`);
        setEditedSegments(null);
        setEditedSegment(null);
        return true;
      }

      throw new Error("Failed to save edited segments");
    } catch (error) {
      console.error("Error saving edited segments:", error);
      alert("Error saving edited segments. Please try again.");
      throw error;
    }
  };

  const controls = (
    <PracticeControlsPanel
      isgranted={isgranted}
      getMicrophonePermission={getMicrophonePermission}
      practiceMode={effectivePracticeMode}
      recordingStatus={recordingStatus}
      recordingPhase={
        loading
          ? "processing"
          : recordingStatus === "recording"
          ? "recording"
          : "idle"
      }
      isPlayingSegment={isPlayingSegment}
      recordingProgress={recordingProgress}
      handlePlayPause={handlePlayPause}
      handleMainAction={handleMainAction}
      getButtonConfig={getButtonConfig}
      rate={rate}
      setRate={setRate}
      audioref={audioref}
      loading={loading}
      echoingPhase={echoingPhase}
    />
  );

  const displayedAssessment = showAssessmentResults ? assessment : null;
  const showPitchPanel =
    !isWriteEcho || writeEchoStage === "review" || writeEchoStage === "result";

  return (
    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-4 xl:p-6 pb-6">
      <SnapsWiseAudio
        audioref={audioref}
        fullaudio={fullaudio}
        timeUpdate={timeUpdate}
      />

      {segments && !editedSegments && (
        <div className="flex flex-col gap-6">
          <SentencePracticeBlock
            segment={segment}
            segments={segments}
            assessment={displayedAssessment}
            renderHelpPill={renderHelpPill}
            renderCommentsPill={renderCommentsPill}
            isMetadataNoHelp={isMetadataNoHelp}
            setShowExerciseHelp={setShowExerciseHelp}
            handlePreviousSegment={handlePreviousSegment}
            handleNextSegment={handleNextSegment}
            showCenterCue={showCenterCue}
            practiceMode={effectivePracticeMode}
            echoingPhase={echoingPhase}
            isCueActive={isCueActive}
            recordingStatus={recordingStatus}
            cuePulseCount={cuePulseCount}
            echoingPulseCount={echoingPulseCount}
            sentenceLabel={sentenceLabel}
            currentWord={currentWord}
            handleWordPlay={handleWordPlay}
            handleRangePlay={handleRangePlay}
            openWordMenu={openWordMenu}
            file={file}
            auth={auth}
            showWords={showTargetText}
            enableWordActions={
              showTargetText && (!isWriteEcho || writeEchoStage === "result")
            }
            isPlayingSegment={isPlayingSegment}
            showWordLane={showMainWordLane}
          />

          {isWriteEcho ? (
            <WriteEchoModePanel
              targetText={segment?.text || ""}
              words={segment?.words || []}
              currentWord={currentWord}
              isPlayingSegment={isPlayingSegment}
              onPlayReference={handlePlayPause}
              onCueWordPlay={handlePlayFromWord}
              rate={rate}
              setRate={setRate}
              assessment={assessment}
              onStageChange={changeWriteEchoStage}
            >
              {controls}
            </WriteEchoModePanel>
          ) : (
            controls
          )}

          {showAssessmentResults && assessment && !assessment.error && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  setRecordingChecked(false);
                  setIsPlayingReview(false);
                  setShowRateMeOverlay(true);
                }}
                className="group px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:-translate-y-0.5 active:scale-[0.98]"
              >
                <span className="inline-flex items-center gap-2">
                  <span>Rate me</span>
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </button>
            </div>
          )}

          {showPitchPanel && (
            <IntonationComparisonPanel
              showIntonationVisualization={showIntonationVisualization}
              showIntonationHelp={showIntonationHelp}
              setShowIntonationHelp={setShowIntonationHelp}
              segment={segment}
              assessment={displayedAssessment}
              subject={subject}
              file={file}
              handleLaneWordClick={showTargetText ? handleLaneWordClick : undefined}
              broadcastTime={broadcastTime}
              audioref={audioref}
              visualizationView={isWriteEcho ? "pitch" : visualizationView}
              rate={rate}
              setRate={setRate}
              auth={auth}
              resolvedAssessmentAudioUrl={resolvedAssessmentAudioUrl}
              resolvedNarratorAssessmentAudioUrl={resolvedNarratorAssessmentAudioUrl}
            />
          )}

          {showAssessmentResults && (
            <AIAssessmentPanel
              assessment={assessment}
              canUsePronunciationApi={canUsePronunciationApi}
              segment={segment}
            />
          )}

          {auth?.role === "ADMIN" && (
            <div className="flex justify-center">
              <button
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-lg transition-all duration-300 font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                onClick={() => {
                  const currentSegment =
                    segments.find((item) => item.id === segment.id) || segments[0];
                  setEditedSegments([...segments]);
                  setEditedSegment({ ...currentSegment });
                }}
              >
                Edit Segments
              </button>
            </div>
          )}
        </div>
      )}

      {editedSegments && (
        <SegmentEditor
          segments={segments}
          editedSegments={editedSegments}
          setEditedSegments={setEditedSegments}
          editedSegment={editedSegment}
          setEditedSegment={setEditedSegment}
          audioref={audioref}
          subject={subject}
          file={file}
          onSave={handleSaveEditedSegments}
          onCancel={() => {
            setEditedSegments(null);
            setEditedSegment(null);
          }}
        />
      )}

      <RateMeOverlay
        open={showRateMeOverlay}
        onClose={() => setShowRateMeOverlay(false)}
        userRecordingUrl={userRecordingUrl}
        recordingChecked={recordingChecked}
        setRecordingChecked={setRecordingChecked}
        isPlayingReview={isPlayingReview}
        setIsPlayingReview={setIsPlayingReview}
        segment={segment}
        assessment={assessment}
        subject={subject}
        file={file}
        chapterTitle={chapterTitle || file?.chapterTitle || file?.title || file?.name || file?.file}
      />
    </div>
  );
};

export default PracticeModeView;